import { api } from '@/services/api';
import i18n from '@/i18n';

/**
 * Cliente do browser para a API da Comunidade Xtreed.
 *
 * Diferente do resto do app, estas chamadas NÃO passam pelo backend da área de
 * membros: o browser fala direto com a comunidade, autenticado por cookie de
 * sessão dela. O cookie é obtido uma vez via SSO (token curto emitido pelo
 * nosso backend) e vale 30 dias.
 *
 * Requisito de produção: a comunidade precisa estar no mesmo domínio
 * registrável da área de membros (ex.: `comunidade.dominio.com` e
 * `app.dominio.com`). O cookie é SameSite=Lax — em domínios diferentes ele não
 * é enviado e todas as chamadas daqui voltam 401 em loop.
 */

export interface SpaceSummary {
  id: string;
  name: string;
  slug: string;
  emoji: string | null;
  description: string | null;
  type: 'POSTS' | 'CALENDAR' | 'RESOURCES';
  locked: boolean;
  canPost: boolean;
  canComment: boolean;
  postCount?: number;
}

export interface CategoryWithSpaces {
  id: string;
  name: string;
  slug: string;
  spaces: SpaceSummary[];
}

export interface SpaceDetail extends SpaceSummary {
  introHtml: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  category: { id: string; name: string; slug: string };
}

export interface AuthorSummary {
  id: string;
  name: string;
  username: string;
  avatarUrl: string | null;
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  reacted: boolean;
}

export interface PostSummary {
  id: string;
  title: string;
  excerpt: string;
  pinned: boolean;
  coverUrl: string | null;
  createdAt: string;
  commentCount: number;
  author: AuthorSummary | null;
  space: { id: string; name: string; slug: string; emoji: string | null };
  reactions: ReactionSummary[];
}

export interface PostDetail extends PostSummary {
  bodyHtml: string;
  canComment: boolean;
}

export interface CommentNode {
  id: string;
  postId: string;
  parentId: string | null;
  bodyHtml: string;
  status: string;
  createdAt: string;
  author: AuthorSummary | null;
  reactions: ReactionSummary[];
  canEdit: boolean;
  canDelete: boolean;
  replies: CommentNode[];
}

export class CommunityError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Uma sessão em voo é compartilhada por todos os chamadores: sem isso, quatro
 * componentes montando juntos disparam quatro SSOs, e o `jti` de uso único faz
 * três deles voltarem SSO_REPLAY.
 */
let sessaoEmVoo: Promise<void> | null = null;

async function requestToken(): Promise<string> {
  const { data } = await api.post('/community/sso-token');
  const token = data?.data?.token ?? data?.token;
  if (!token)
    throw new CommunityError(i18n.t('community:errors.accessToken'), 500);
  return token;
}

async function openSession(apiUrl: string): Promise<void> {
  const send = (token: string) =>
    fetch(`${apiUrl}/api/auth/sso`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ token }),
    });

  let response = await send(await requestToken());

  // 409 = já existe sessão de OUTRO usuário no browser (trocou de conta na área
  // de membros). Derrubamos a antiga e refazemos — mas com um token novo, já
  // que o anterior foi consumido.
  if (response.status === 409) {
    await fetch(`${apiUrl}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => undefined);
    response = await send(await requestToken());
  }

  if (!response.ok) {
    const bodyText = await response.json().catch(() => ({}));
    throw new CommunityError(
      bodyText?.message || i18n.t('community:errors.signIn'),
      response.status,
    );
  }
}

export async function endSession(apiUrl: string): Promise<void> {
  sessaoEmVoo = null;
  await fetch(`${apiUrl}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined);
}

export function ensureSession(apiUrl: string): Promise<void> {
  if (!sessaoEmVoo) {
    sessaoEmVoo = openSession(apiUrl).catch((error) => {
      sessaoEmVoo = null;
      throw error;
    });
  }
  return sessaoEmVoo;
}

async function request<T>(
  apiUrl: string,
  path: string,
  init?: RequestInit,
  alreadyRetried = false,
): Promise<T> {
  await ensureSession(apiUrl);

  const result = await fetch(`${apiUrl}/api${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (result.status === 401 && !alreadyRetried) {
    sessaoEmVoo = null;
    return request<T>(apiUrl, path, init, true);
  }

  if (!result.ok) {
    const errorBody = await result.json().catch(() => ({}));
    throw new CommunityError(
      errorBody?.message || i18n.t('community:errors.noResponse'),
      result.status,
    );
  }

  if (result.status === 204) return undefined as T;
  return result.json() as Promise<T>;
}

/** Árvore de categorias e espaços visíveis — alimenta a navegação da folha. */
export function fetchCategories(apiUrl: string) {
  return request<CategoryWithSpaces[]>(apiUrl, '/categories');
}

export function fetchSpace(apiUrl: string, slug: string) {
  return request<SpaceDetail>(apiUrl, `/spaces/${slug}`);
}

export function fetchPosts(apiUrl: string, slug: string, limit = 20) {
  return request<{ items: PostSummary[]; nextCursor: string | null }>(
    apiUrl,
    `/posts?spaceSlug=${encodeURIComponent(slug)}&limit=${limit}`,
  );
}

export function fetchPost(apiUrl: string, postId: string) {
  return request<PostDetail>(apiUrl, `/posts/${postId}`);
}

export function fetchComments(apiUrl: string, postId: string) {
  return request<{ items: CommentNode[] }>(apiUrl, `/posts/${postId}/comments`);
}

export function createComment(
  apiUrl: string,
  postId: string,
  bodyMd: string,
  parentId?: string,
) {
  return request<CommentNode>(apiUrl, `/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ bodyMd, ...(parentId ? { parentId } : {}) }),
  });
}

export function createPost(
  apiUrl: string,
  entry: { spaceId: string; title: string; bodyMd: string },
) {
  return request<PostDetail>(apiUrl, '/posts', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/** Toggle: a mesma chamada adiciona e remove a reação do usuário. */
export function reactToPost(apiUrl: string, postId: string, emoji: string) {
  return request<ReactionSummary[]>(apiUrl, `/posts/${postId}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

export const REACTION_EMOJIS = ['👍', '❤️', '🔥', '🚀', '👏'] as const;
